/**
 * Account deletion monitor.
 *
 * Read-only. account_deletions is the only evidence that a deleted user's RevenueCat
 * identity was cleared and their Firestore data swept, and both steps run after the app
 * has already told the user "deleted" -- so a silent failure reaches nobody. This page
 * and the hourly retryAccountDeletions function are what make one visible.
 *
 * Recovery is deliberately not here: firestore.rules denies every client write to this
 * collection, and the reconciler runs the same runAccountDeletion() the live trigger
 * runs. A retry button would be a second implementation of the erasure to keep in step.
 */
import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { RefreshCw } from 'lucide-react';
import Button from '../../components/Button';
import ErrorDisplay from '../../components/ErrorDisplay';
import LoadingSpinner from '../../components/LoadingSpinner';
import { AccountDeletionsTable } from './components/AccountDeletionsTable';
import { DeletionDetailModal } from './components/DeletionDetailModal';
import { DeletionFiltersBar } from './components/DeletionFiltersBar';
import { useAccountDeletions } from './hooks/useAccountDeletions';
import { RECENT_LIMIT } from './services/accountDeletionsService';
import {
  filtersFromParams,
  filtersToParams,
  matchesFilters,
  type DeletionFilters,
} from './utils/deletionFilters';

interface CountTileProps {
  label: string;
  value: number;
  tone: 'neutral' | 'good' | 'warn' | 'bad';
  hint: string;
  onClick?: () => void;
}

const TONE_CLASSES: Record<CountTileProps['tone'], string> = {
  neutral: 'text-blue-700',
  good: 'text-green-700',
  warn: 'text-amber-700',
  bad: 'text-red-700',
};

const CountTile = ({ label, value, tone, hint, onClick }: CountTileProps) => (
  <button
    type="button"
    onClick={onClick}
    title={hint}
    className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 text-left hover:shadow-md transition-shadow"
  >
    <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">{label}</p>
    <p className={`mt-1 text-2xl font-semibold ${TONE_CLASSES[tone]}`}>{value}</p>
  </button>
);

export const AccountDeletions = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { records, counts, loading, error, reload } = useAccountDeletions();
  const [selectedUid, setSelectedUid] = useState<string | null>(null);

  const filters = useMemo(() => filtersFromParams(searchParams), [searchParams]);

  const setFilters = (next: DeletionFilters) => {
    setSearchParams(filtersToParams(next));
  };

  const visible = useMemo(
    () => records.filter((record) => matchesFilters(record, filters)),
    [records, filters],
  );

  // Held by uid rather than by value, so a reload re-renders the modal from the fresh list.
  const selected = selectedUid
    ? (records.find((record) => record.uid === selectedUid) ?? null)
    : null;

  if (loading) return <LoadingSpinner message="Loading account deletions…" />;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto p-4 sm:p-6">
        <div className="flex flex-wrap items-start justify-between gap-3 mb-1">
          <h1 className="text-3xl font-bold text-gray-900">Account deletions</h1>
          <Button
            variant="outline"
            size="sm"
            icon={<RefreshCw size={13} />}
            onClick={() => void reload()}
          >
            Refresh
          </Button>
        </div>
        <p className="text-sm text-gray-500 mb-6">
          The erasure audit trail. RevenueCat is anonymized first, the PostHog person is deleted
          second, then Firestore is swept. All three run
          after the user was told the account was deleted, so a failure here is silent everywhere
          else.
        </p>

        {error && <ErrorDisplay message={error} />}

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <CountTile
            label="In progress"
            value={counts.inProgress}
            tone="neutral"
            hint="Open jobs. Normal for a few minutes after a deletion."
            onClick={() => setFilters({ ...filters, status: 'in_progress' })}
          />
          <CountTile
            label="Completed"
            value={counts.completed}
            tone="good"
            hint="RevenueCat anonymized, PostHog person deleted and every registered location swept."
            onClick={() => setFilters({ ...filters, status: 'completed' })}
          />
          <CountTile
            label="Failed"
            value={counts.failed}
            tone={counts.failed > 0 ? 'bad' : 'good'}
            hint="The reconciler retries these hourly, 50 per run."
            onClick={() => setFilters({ ...filters, status: 'failed' })}
          />
          <CountTile
            label="Stalled"
            value={counts.stalled}
            tone={counts.stalled > 0 ? 'warn' : 'good'}
            hint="Unfinished and untouched for over two hours, so a reconciler run has already missed them."
            onClick={() => setFilters({ ...filters, status: 'attention' })}
          />
        </div>

        <DeletionFiltersBar
          filters={filters}
          onChange={setFilters}
          shown={visible.length}
          total={records.length}
        />

        <AccountDeletionsTable
          records={visible}
          onOpen={(record) => setSelectedUid(record.uid)}
        />

        {records.length >= RECENT_LIMIT && (
          <p className="mt-3 text-xs text-gray-500">
            Showing the {RECENT_LIMIT} most recently updated records. The counts above are
            server-side totals, so they cover everything.
          </p>
        )}

        <DeletionDetailModal record={selected} onClose={() => setSelectedUid(null)} />
      </div>
    </div>
  );
};
